import { createActions } from 'redux-actions';

import { ENDPOINT_VERSION, ENDPOINTS_SIMULATIONS } from '../../config';
import { localeData } from '../../locales/data';
import { SimulationsPostValidation } from '../../reducers/EditorReducer/EditorReducer.types';
import { ERROR_STATUS, FAQ_URL, NEW_STATUS, SIMULATING_STATUS, SUPPORT_URL_PLACEHOLDER, WARNING_STATUS } from '../../shared/constants';
import { checkIfIsValid, cleanTabFlashMessages, getEditorTabID, prefixEditTabMsg } from '../../shared/helpers';
import { globalFetch, isAction } from '../../shared/helpers/fetch-helper';
import { logErrorService } from '../../shared/services';
import { sessionExpire } from '../AuthenticationActions';
import { setTabCodeError } from '../EditorActions/EditorActions';
import { addFlashMessages, removeFlashContexts } from '../FlashMessagesActions';
import { invalidRequest } from '../GenericActions';

import { changeSimulationStatus, getAlphasFromAlpha, getSimulationData, responseDataSimulateSuccess } from './DataAction';
import { makeLinkRenderer } from '../../shared/helpers/internationalization';
import analytics from '../../shared/services/analytics/analytics';
import { getIsTutorialModeActiveSelector, getMaxUnlockedTutorialStep } from '../../reducers/TutorialReducer/TutorialSelectors';
import { toggleSimulationFilterThunk } from './SimulationFiltersActions';
import type { History } from 'history';
import {
  getConsultantCurrentlyViewedStep,
  getConsultantIsTutorialModeActiveSelector,
  getConsultantTutorialSteps,
} from '../../reducers/ConsultantTutorialReducer/ConsultantTutorialSelectors';
import { TutorialStepRequirement } from '../../shared/api/api';

export const { responseSimulationSuccess, requestSimulationPost, responseSimulationFailure } = createActions({
  RESPONSE_SIMULATION_SUCCESS: (idParent: string, data: string) => ({ idParent, data }),
  RESPONSE_SIMULATION_FAILURE: (idParent: string) => ({ idParent }),
  REQUEST_SIMULATION_POST: (idParent: string, indexChild: any, isTutorialModeActive?: boolean, tutorialStep?: boolean) => ({
    idParent,
    indexChild,
    isTutorialModeActive,
    tutorialStep,
  }),
});

export const {
  responseProgressSimulateSuccess,
  requestProgressSimulate,
  responseProgressSimulateFailure,
  responseProgressSimulateComplete,
  hideLearnBox,
  cleanProgress,
} = createActions(
  {
    RESPONSE_PROGRESS_SIMULATE_SUCCESS: (percentage: number, idParent: string) => ({ percentage, idParent }),
    REQUEST_PROGRESS_SIMULATE: (idParent: string) => ({ idParent }),
    RESPONSE_PROGRESS_SIMULATE_FAILURE: (idParent: string) => ({ idParent }),
    RESPONSE_PROGRESS_SIMULATE_COMPLETE: (idParent: string) => ({ idParent }),
    HIDE_LEARN_BOX: (idParent: string) => ({ idParent }),
  },
  'CLEAN_PROGRESS'
);

export const { cancelSimulationRequest, cancelSimulationSuccess, cancelSimulationFailure, cancelSimulationDone } = createActions({
  CANCEL_SIMULATION_REQUEST: (idParent: string) => ({ idParent }),
  CANCEL_SIMULATION_SUCCESS: (idParent: string) => ({ idParent }),
  CANCEL_SIMULATION_FAILURE: (idParent: string) => ({ idParent }),
  CANCEL_SIMULATION_DONE: (idParent: string) => ({ idParent }),
});

export const cancelSimulation =
  (simulationId: string, idParent: string) =>
  async (dispatch: any): Promise<object> => {
    if (simulationId === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(cancelSimulationRequest(idParent));

    return fetch(`${ENDPOINTS_SIMULATIONS.simulations}/${simulationId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any) => {
        if (response.ok) {
          return response.json();
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);

          dispatch(sessionExpire());

          return dispatch(cancelSimulationFailure(idParent));
        } else {
          return response.json();
        }
      })
      .then((json: any) => {
        if (json[0] === 'Can not delete complete simulations') {
          dispatch(cancelSimulationDone());
        } else {
          dispatch(cancelSimulationFailure(idParent));
          dispatch(
            addFlashMessages([{ msg: 'api.error.simulation-later', defaultMessage: 'api.error.simulation-later' }], {
              context: `${prefixEditTabMsg}${idParent}`,
              location: 'editor-code',
            })
          );
        }

        return dispatch(cancelSimulationDone());
      })
      .catch(() => {
        dispatch(cancelSimulationSuccess(idParent));

        return dispatch(cancelSimulationDone());
      });
  };

const getCtxsToRemove = (childTabIndex: number | null, id: string, state: any) => {
  const contexts = [`${prefixEditTabMsg}${id}`];

  if (childTabIndex !== null) {
    const activeTab = state.getIn(['editor', 'activeTab']);
    const parentTab = state.getIn(['editor', 'tabs', activeTab, 'childTabs']);
    if (parentTab) {
      const childTab = parentTab.getIn([childTabIndex]);

      if (childTab) {
        return [`${prefixEditTabMsg}${id}`, `${prefixEditTabMsg}${childTab.get('id')}`];
      } else {
        return contexts;
      }
    } else {
      return contexts;
    }
  } else {
    return contexts;
  }
};

const makeRenderersFromLinks = (links: Record<string, string>) => {
  return Object.keys(links).reduce(
    (intValues, key) => ({
      ...intValues,
      [key]: makeLinkRenderer({ href: links[key], isOpenInNewTab: true }),
    }),
    {}
  );
};

export function getSimulation(
  id: string,
  location: string,
  timeOutValue: number,
  childTabIndex: any,
  isVisualization: boolean,
  history: History
) {
  return async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    const ctxToRemove = getCtxsToRemove(childTabIndex, id, state);

    dispatch(removeFlashContexts(['editor-code'], ctxToRemove));

    if (state.getIn(['simulation', 'progress', id, 'cancel'])) {
      dispatch(responseProgressSimulateFailure(id));
      dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, ERROR_STATUS));

      return dispatch(setTabCodeError(null, id, childTabIndex));
    } else {
      dispatch(requestProgressSimulate(id));

      return globalFetch(
        location,
        {
          method: 'GET',
          credentials: 'include',
          headers: new Headers({ 'Content-Type': 'application/json', 'Access-Control-Request-Headers': 'Location' }),
        },
        dispatch,
        () => dispatch(responseProgressSimulateFailure(id)),
        {
          callbackAction: () => dispatch(getSimulation(id, location, timeOutValue, childTabIndex, isVisualization, history)),
        }
      )
        .then((json: any) => {
          const tabID = getEditorTabID(id, childTabIndex, state);
          if (isAction(json, 'RESPONSE_PROGRESS_SIMULATE_FAILURE')) {
            return json;
          } else if (json.details) {
            dispatch(
              addFlashMessages([{ msg: json.details, defaultMessage: json.details }], {
                context: `${prefixEditTabMsg}${tabID}`,
                location: 'editor-code',
              })
            );

            logErrorService(json.details, json.details);
            dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, ERROR_STATUS));
            dispatch(setTabCodeError(null, id, childTabIndex));

            return dispatch(responseProgressSimulateFailure(id));
          } else if (json.detail === 'Not found.') {
            logErrorService(json.detail, json.detail);
            dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, ERROR_STATUS));
            dispatch(setTabCodeError(null, id, childTabIndex));

            return dispatch(responseProgressSimulateFailure(id));
          } else if (json.status === 'ERROR' && json.children === undefined) {
            const msg = json.message
              ? {
                  msg: json.message,
                  defaultMessage: json.message,
                  intlValues: json.links ? makeRenderersFromLinks(json.links) : undefined,
                }
              : {
                  msg: 'api.error.simulation-alpha',
                  defaultMessage: localeData.en['api.error.simulation-alpha'],
                };

            dispatch(removeFlashContexts(['editor-code'], [`${prefixEditTabMsg}${tabID}`]));
            dispatch(
              addFlashMessages([msg], {
                context: `${prefixEditTabMsg}${tabID}`,
                location: 'editor-code',
              })
            );
            logErrorService(json, json);
            if (json.location) {
              dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, ERROR_STATUS));
              dispatch(setTabCodeError(json.location, id, childTabIndex));
            } else {
              dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, ERROR_STATUS));
              dispatch(setTabCodeError(null, id, childTabIndex));
            }

            return dispatch(responseProgressSimulateFailure(id));
          } else if (json.status === 'TIMEOUT') {
            const msg = 'api.error.simulation-python-loops';
            dispatch(
              addFlashMessages([{ msg, defaultMessage: localeData.en[msg] }], {
                context: `${prefixEditTabMsg}${tabID}`,
                location: 'editor-code',
              })
            );
            logErrorService(json, json);
            dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, ERROR_STATUS));
            dispatch(setTabCodeError(null, id, childTabIndex));

            return dispatch(responseProgressSimulateFailure(id));
          } else if (json.status === 'FAIL') {
            dispatch(
              addFlashMessages([{ msg: 'api.error.brain.experiencing', defaultMessage: localeData.en['api.error.brain.experiencing'] }], {
                links: [
                  {
                    urls: [SUPPORT_URL_PLACEHOLDER],
                    keys: ['api.error.brain.experiencing.link'],
                    defaultMsgkeys: [localeData.en['api.error.brain.experiencing.link']],
                  },
                ],
                context: `${prefixEditTabMsg}${tabID}`,
                location: 'editor-code',
              })
            );
            logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);
            dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, ERROR_STATUS));
            dispatch(setTabCodeError(null, id, childTabIndex));

            return dispatch(responseProgressSimulateFailure(id));
          } else if (json.detail) {
            dispatch(
              addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }], {
                context: `${prefixEditTabMsg}${tabID}`,
                location: 'editor-code',
              })
            );
            logErrorService(json.detail, json.detail);
            dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, ERROR_STATUS));
            dispatch(setTabCodeError(null, id, childTabIndex));

            return dispatch(responseProgressSimulateFailure(id));
          } else if (json.progress >= 0) {
            dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, NEW_STATUS));

            return dispatch(responseProgressSimulateSuccess(json.progress.toFixed(2), id));
          } else if (json.children) {
            dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, NEW_STATUS));
            dispatch(setTabCodeError(null, id, childTabIndex));
            dispatch(responseProgressSimulateSuccess(1, id));
            setTimeout(() => {
              json.children.map((alpha: string, index: number) =>
                dispatch(getSimulation(id, `${ENDPOINTS_SIMULATIONS.simulations}/${alpha}`, timeOutValue, index, isVisualization, history))
              );
            }, 1000);
          } else {
            if (json.status === 'WARNING') {
              const msg = json.message
                ? {
                    msg: json.message,
                    defaultMessage: json.message,
                    intlValues: json.links ? makeRenderersFromLinks(json.links) : undefined,
                  }
                : {
                    msg: 'api.error.simulation-alpha',
                    defaultMessage: localeData.en['api.error.simulation-alpha'],
                  };

              dispatch(removeFlashContexts(['editor-code'], [`${prefixEditTabMsg}${tabID}`]));
              dispatch(
                addFlashMessages([msg], {
                  context: `${prefixEditTabMsg}${tabID}`,
                  location: 'editor-code',
                  type: 'warning',
                })
              );
              if (json.location) {
                dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, WARNING_STATUS));
                dispatch(setTabCodeError(json.location, id, childTabIndex));
              } else {
                dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, WARNING_STATUS));
                dispatch(setTabCodeError(null, id, childTabIndex));
              }
            } else {
              dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, NEW_STATUS));
            }
            dispatch(responseProgressSimulateSuccess(1, id));
            setTimeout(() => {
              if (!json) {
                dispatch(responseDataSimulateSuccess(json));
              } else {
                const tabIndex = state.getIn(['editor', 'tabs']).findIndex((item: any) => item.get('id') === id);
                const indexChild = state.getIn(['editor', 'tabs', tabIndex, 'childTabs', childTabIndex, 'id']);
                dispatch(getSimulationData(id, indexChild, json.alpha, isVisualization));
                if (json.type === 'SUPER') {
                  dispatch(getAlphasFromAlpha(id, json.alpha));
                }
              }
              dispatch(responseProgressSimulateComplete(id));
            }, 1000);
          }
        })
        .catch(() => {
          dispatch(changeSimulationStatus(id, SIMULATING_STATUS, childTabIndex, ERROR_STATUS));
          dispatch(setTabCodeError(null, id, childTabIndex));

          dispatch(
            addFlashMessages([{ msg: 'api.error.simulation-alpha', defaultMessage: localeData.en['api.error.simulation-alpha'] }], {
              context: `${prefixEditTabMsg}${getEditorTabID(id, childTabIndex, state)}`,
              location: 'editor-code',
            })
          );
          logErrorService(localeData.en['api.error.simulation-alpha'], localeData.en['api.error.simulation-alpha']);

          return dispatch(responseProgressSimulateFailure(id));
        });
    }
  };
}

const fetchSimulationPostErrorStatus: any = [503, 403, 429];

const fetchSimulationPostErrorMessages = (status: number, data: { detail: string }) => {
  switch (status) {
    case 503:
      return {
        message: [
          {
            msg: 'api.error.simulation-post-503',
            defaultMessage: localeData.en['api.error.simulation-post-503'],
          },
        ],
        options: {
          links: [
            {
              urls: [SUPPORT_URL_PLACEHOLDER],
              keys: ['api.error.simulation-post-503-link'],
              defaultMsgkeys: [localeData.en['api.error.simulation-post-503-link']],
            },
          ],
        },
      };
    case 429:
      if (data.detail === 'DAILY_SIMULATION_LIMIT_EXCEEDED') {
        return {
          message: [
            {
              msg: 'api.error.simulation-post-429-daily',
              defaultMessage: localeData.en['api.error.simulation-post-429-daily'],
            },
          ],
          options: {
            links: [
              {
                urls: [
                  `${FAQ_URL}/articles/18423410021783--BRAIN-TIPS-Troubleshooting-common-error-messages#:~:text=Daily%20simulation%20limit%20reached.%20Please%20try%20tomorrow%20(EST%20time%20zone)`,
                ],
                keys: ['api.error.simulation-post-429-daily.link'],
                defaultMsgkeys: [localeData.en['api.error.simulation-post-429-daily.link']],
              },
            ],
          },
        };
      }
      return {
        message: [
          {
            msg: 'api.error.simulation-post-429',
            defaultMessage: localeData.en['api.error.simulation-post-429'],
          },
        ],
        options: {
          links: [
            {
              urls: [`${FAQ_URL}/articles/18423410021783--BRAIN-TIPS-Troubleshooting-common-error-messages`],
              keys: ['api.error.simulation-post-429.link'],
              defaultMsgkeys: [localeData.en['api.error.simulation-post-429.link']],
            },
          ],
        },
      };
    case 403:
      return {
        message: [
          {
            msg: 'api.error.simulation-post-403',
            defaultMessage: localeData.en['api.error.simulation-post-403'],
          },
        ],
      };
    default:
      return {
        message: [
          {
            msg: 'api.error.brain.experiencing',
            defaultMessage: localeData.en['api.error.brain.experiencing'],
          },
        ],
        options: {
          links: [
            {
              urls: [SUPPORT_URL_PLACEHOLDER],
              keys: ['api.error.brain.experiencing.link'],
              defaultMsgkeys: [localeData.en['api.error.brain.experiencing.link']],
            },
          ],
        },
      };
  }
};

const simulationBadRequest = (headers: any, dispatch: any, tabID: any) => {
  let messages: string[] = [];
  Object.keys(headers.settings).map((el: string) => {
    messages = messages.concat(headers.settings[el]);
  });
  const flashMessages = messages.map((msg: string) => ({ msg, defaultMessage: msg }));
  dispatch(addFlashMessages(flashMessages, { context: `${prefixEditTabMsg}${tabID}`, location: 'editor-code' }));
  logErrorService(flashMessages, flashMessages);
};

interface FetchSimulationPostParams {
  id: string;
  indexChild: any;
  data: any;
  isVisualization: boolean;
  history: History;
  currentActiveTab: number;
}

const checkedRequirements = new Set<TutorialStepRequirement>(['submit', 'simulate']);

export function fetchSimulationPost({ id, indexChild, data, isVisualization, history, currentActiveTab }: FetchSimulationPostParams) {
  return (dispatch: any, getState: any) => {
    // Determine simulation type for analytics: multi (data is an array of simulations),
    // super (data has type === 'SUPER'), or regular otherwise.
    let simulationType: 'multi' | 'super' | 'regular' = 'regular';
    if (Array.isArray(data)) {
      simulationType = 'multi';
    } else if (data && data.type === 'SUPER') {
      simulationType = 'super';
    }
    analytics.trackCustomEvent('simulate', { event_label: simulationType });

    const state = getState();

    const maxUnlockedTutorialStep = getMaxUnlockedTutorialStep(state);
    const isConsultantTutorialActive = getConsultantIsTutorialModeActiveSelector(state);
    const consultantTutorialStep = getConsultantCurrentlyViewedStep(state);
    const isConsultantStepChecked =
      isConsultantTutorialActive && checkedRequirements.has(getConsultantTutorialSteps(state)[consultantTutorialStep].requirements);
    const isTutorialModeActive = getIsTutorialModeActiveSelector(state) || isConsultantStepChecked;
    cleanTabFlashMessages({ id, state, dispatch, removeFlashContexts });
    const tutorialStep = isConsultantTutorialActive ? consultantTutorialStep : maxUnlockedTutorialStep;
    dispatch(requestSimulationPost(id, indexChild, isTutorialModeActive, tutorialStep));
    let multiTabValid = false;
    if (Array.isArray(data)) {
      multiTabValid = data.filter((simulation: any) => !checkIfIsValid(simulation, SimulationsPostValidation)).length === 0;
    }

    //this check will only send errors to sentry and console
    if (!multiTabValid) {
      checkIfIsValid(data, SimulationsPostValidation);
    }

    if (state.getIn(['simulation', 'filters', currentActiveTab, 'results']) !== true) {
      dispatch(toggleSimulationFilterThunk('results', currentActiveTab, history));
    }

    return fetch(ENDPOINTS_SIMULATIONS.simulations, {
      method: 'POST',
      credentials: 'include',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Access-Control-Request-Headers': 'Location',
        Accept: `application/json;${ENDPOINT_VERSION}`,
      }),
      body: JSON.stringify(data),
    })
      .then(async (response: Response) => {
        const tabID = getEditorTabID(id, indexChild, state);
        if (response.ok) {
          return response.headers;
        } else if (fetchSimulationPostErrorStatus.includes(response.status)) {
          const errorMsg: any = fetchSimulationPostErrorMessages(response.status, await response.json());
          if (errorMsg.hasOwnProperty('options')) {
            dispatch(
              addFlashMessages(errorMsg.message, {
                ...errorMsg.options,
                ...{ context: `${prefixEditTabMsg}${tabID}`, location: 'editor-code' },
              })
            );
          } else {
            dispatch(addFlashMessages(errorMsg.message, { context: `${prefixEditTabMsg}${tabID}`, location: 'editor-code' }));
          }
          logErrorService(errorMsg.message, errorMsg.message);

          return dispatch(responseSimulationFailure(id));
        } else {
          return response.json();
        }
      })
      .then((headers: any) => {
        const tabID = getEditorTabID(id, indexChild, state);
        if (headers && headers.type === 'RESPONSE_SIMULATION_FAILURE') {
          return headers;
        } else if (Array.isArray(headers) && headers[0].settings) {
          headers.map((wrongSettings: any, index: any) => {
            const childId = getEditorTabID(id, index, state);
            simulationBadRequest(wrongSettings, dispatch, childId);
          });

          return dispatch(responseSimulationFailure(id));
        } else if (headers.settings) {
          simulationBadRequest(headers, dispatch, tabID);

          return dispatch(responseSimulationFailure(id));
        } else if (headers.detail) {
          dispatch(
            addFlashMessages([{ msg: headers.detail, defaultMessage: headers.detail }], {
              context: `${prefixEditTabMsg}${tabID}`,
              location: 'editor-code',
            })
          );
          logErrorService(headers.detail, headers.detail);

          return dispatch(responseSimulationFailure(id));
        } else if (headers.errors) {
          dispatch(addFlashMessages(headers.errors, { context: `${prefixEditTabMsg}${tabID}`, location: 'editor-code', intl: false }));
          logErrorService(headers.errors, headers.errors);

          return dispatch(responseSimulationFailure(id));
        }
        const location = headers.get('Location');
        const timeOut = headers.get('Retry-After');
        if (location && timeOut) {
          dispatch(getSimulation(id, location, parseFloat(timeOut) * 1000, null, isVisualization, history));
          const simulationId = location.match(/simulations\/(.*)/) || '';
          return dispatch(responseSimulationSuccess(id, simulationId[1]));
        } else if (location) {
          dispatch(getSimulation(id, location, 0, null, isVisualization, history));

          return dispatch(responseSimulationSuccess(id));
        }
      })
      .catch(() => {
        const msg = 'api.error.simulation-later';
        dispatch(
          addFlashMessages([{ msg, defaultMessage: localeData.en[msg] }], {
            context: `${prefixEditTabMsg}${getEditorTabID(id, indexChild, state)}`,
            location: 'editor-code',
          })
        );
        logErrorService(localeData.en[msg], localeData.en[msg]);

        return dispatch(responseSimulationFailure(id));
      });
  };
}
